import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { IconButton, LinearProgress, Menu, MenuItem, MenuList, useMediaQuery } from '@mui/material';
import { useEffect, useState } from 'react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
import GridViewIcon from '@mui/icons-material/GridView';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';
import UserIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { useTitle } from '../../../hooks/titleProvider';
import { useAuth } from '../../../contexts/authContext';
import { useColorMode } from '../../../hooks/colorModeProvider';

import { signOutUser } from '../../../services/auth';
import { useAlert } from '../../../contexts/alertContext';

import BowlscoutLogo from '../../../resources/assets/images/bowlscout_logo_text_next.png';
import BowlscoutWhiteLogo from '../../../resources/assets/images/bowlscout_logo_text_next_white.png';
import { useNavigate } from 'react-router-dom';
import { hasAccess, Roles } from '../../../helpers/access';
import { useDispatch, useSelector } from 'react-redux';
import { getOngoingEvents } from '../../../features/event/eventSlice';
import AiSidebar from '../../ai/aiSidebar';

const drawerWidth = 240;

export default function MainNavigation({ children }) {
  const dispatch = useDispatch();
  const ongoingEvents = useSelector((state) => state.event.ongoingEvents) || [];
  const { title } = useTitle();
  const { authenticated, currentUserProfile, workspaces, currentWorkspace, switchWorkspace } =
    useAuth();
  const { mode, toggleColorMode } = useColorMode();
  const { showAlertError, showAlertSuccess } = useAlert();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const activeMenuItem = window.location.pathname;

  const navItems = [
    {
      text: 'Overview',
      secondaryText: '',
      link: '/overview',
      icon: <DashboardIcon fontSize="small" />,
      access: Roles.USER
    },
    // {
    //   text: 'Logs',
    //   secondaryText: '',
    //   link: '/logs',
    //   icon: <RankingIcon color="lightIcon" />
    // },
    // {
    //   text: 'New Private Game',
    //   secondaryText: '',
    //   link: '/new',
    //   icon: <GamesIcon color="lightIcon" />,
    //   active: false,
    //   access: Roles.USER
    // },
    {
      text: 'Events',
      secondaryText: '',
      link: '/events',
      icon: <EventIcon fontSize="small" />,
      access: Roles.USER
    },
    {
      text: 'Green Patterns',
      secondaryText: '',
      link: '/green-patterns',
      icon: <GridViewIcon fontSize="small" />,
      access: Roles.USER
    },
    {
      text: 'Workspaces',
      secondaryText: '',
      link: '/workspace-settings',
      icon: <WorkspacesIcon fontSize="small" />,
      access: Roles.USER
    },
    {
      text: 'Support',
      secondaryText: '',
      link: '/support',
      icon: <SupportAgentIcon fontSize="small" />,
      access: Roles.USER
    }
    // {
    //   text: 'Tournaments',
    //   secondaryText: '',
    //   link: '/tournaments',
    //   icon: <GamesIcon color="lightIcon" />,
    //   access: Roles.USER
    // }
  ];

  const userMenuItems = [
    {
      text: 'Profile',
      secondaryText: '',
      link: '/profile',
      icon: <UserIcon fontSize="small" />,
      access: Roles.USER
    },
    {
      text: 'Settings',
      secondaryText: '',
      link: '/settings',
      icon: <SettingsIcon fontSize="small" />,
      access: Roles.SUPERUSER
    },
    {
      text: 'Support',
      secondaryText: '',
      link: '/support',
      icon: <SupportAgentIcon fontSize="small" />,
      access: Roles.USER
    },
    {
      text: 'Sign Out',
      secondaryText: '',
      link: '/signout',
      icon: <LogoutIcon fontSize="small" />,
      access: Roles.USER
    }
  ];

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState(null);

  useEffect(() => {
    if (authenticated && ongoingEvents.length === 0) {
      dispatch(getOngoingEvents());
    }
  }, [authenticated, dispatch, ongoingEvents.length]);

  const getDate = (value) => {
    if (!value) return null;
    if (value?.toDate && typeof value.toDate === 'function') return value.toDate();

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getEventProgress = (event) => {
    const startDate = getDate(event.startDate);
    const endDate = getDate(event.endDate);

    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    const totalMs = end.getTime() - start.getTime();
    if (totalMs <= 0) return 100;

    const now = Date.now();
    const elapsedMs = now - start.getTime();
    const percent = Math.round((elapsedMs / totalMs) * 100);

    return Math.min(100, Math.max(0, percent));
  };

  const fullName = currentUserProfile.firstName + ' ' + currentUserProfile.lastName;
  const workspaceLabel =
    currentWorkspace?.name || currentUserProfile?.defaultWorkspaceName || 'My Workspace';
  const canOpenWorkspaceSettings =
    String(currentUserProfile?.role || 'user')
      .trim()
      .toLowerCase() !== 'user';

  const handleSignOut = async () => {
    await signOutUser()
      .then(() => {
        showAlertSuccess('Successfully signed out!');
      })
      .catch((err) => {
        showAlertError('Error signing out. Please try again.');
      });
  };

  const handleSwitchWorkspace = async (workspaceId) => {
    try {
      await switchWorkspace(workspaceId);
      await dispatch(getOngoingEvents());
    } catch (error) {
      showAlertError(error?.response?.data || error?.message || 'Failed to switch workspace.');
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AiSidebar />
      <AppBar
        position="fixed"
        sx={
          !isMobile
            ? {
                width: `calc(100% - ${drawerWidth}px)`,
                ml: `${drawerWidth}px`,
                bgcolor: 'background.paper',
                color: 'text.primary',
                boxShadow: 'none',
                border: 0
              }
            : {
                width: '100%',
                bgcolor: 'background.paper',
                color: 'text.primary',
                boxShadow: 'none',
                border: 0
              }
        }>
        <Toolbar>
          {isMobile && (
            <IconButton
              edge="start"
              color="default"
              sx={{ color: 'text.primary' }}
              aria-label="menu"
              onClick={() => {
                setUserMenuAnchor(null);
                setMobileMenuOpen(!mobileMenuOpen);
              }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <Box display={'flex'} justifyContent={'flex-end'}>
            <IconButton
              sx={{ ml: 1, color: 'text.primary' }}
              onClick={toggleColorMode}
              color="default">
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Box>
          {isMobile && (
            <IconButton
              edge="end"
              color="default"
              sx={{ color: 'text.primary' }}
              onClick={(e) => {
                setUserMenuAnchor(e.currentTarget);
                setUserMenuOpen(true);
              }}>
              <UserIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {!isMobile && (
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }
          }}
          variant="permanent"
          anchor="left">
          <Toolbar sx={{ display: 'flex', justifyContent: 'center' }}>
            {/* Logo Image */}
            {mode === 'dark' ? (
              <img src={BowlscoutWhiteLogo} alt="Logo" style={{ width: '150px', height: 'auto' }} />
            ) : (
              <img src={BowlscoutLogo} alt="Logo" style={{ width: '150px', height: 'auto' }} />
            )}
          </Toolbar>
          <Divider />
          <MenuList>
            {authenticated && currentUserProfile && (
              <>
                <MenuItem
                  onClick={(e) => setWorkspaceMenuAnchor(e.currentTarget)}
                  sx={{
                    borderRadius: 2,
                    mx: 1,
                    my: 0.5,
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}>
                  <ListItemIcon sx={{ color: 'text.secondary' }}>
                    <WorkspacesIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={workspaceLabel}
                    secondary={'Current workspace'}
                    primaryTypographyProps={{ noWrap: true }}
                  />
                  <KeyboardArrowDownIcon fontSize="small" color="action" />
                </MenuItem>
                <Menu
                  anchorEl={workspaceMenuAnchor}
                  open={Boolean(workspaceMenuAnchor)}
                  onClose={() => setWorkspaceMenuAnchor(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}>
                  {(workspaces || []).map((workspace) => (
                    <MenuItem
                      key={workspace.id}
                      selected={workspace.id === currentWorkspace?.id}
                      onClick={async () => {
                        setWorkspaceMenuAnchor(null);
                        await handleSwitchWorkspace(workspace.id);
                      }}>
                      <ListItemText
                        primary={workspace.name || 'Workspace'}
                        secondary={workspace.id === currentWorkspace?.id ? 'Active' : ''}
                      />
                    </MenuItem>
                  ))}
                  {canOpenWorkspaceSettings ? <Divider /> : null}
                  {canOpenWorkspaceSettings ? (
                    <MenuItem
                      onClick={() => {
                        setWorkspaceMenuAnchor(null);
                        navigate('/workspace-settings');
                      }}>
                      <ListItemIcon sx={{ color: 'text.secondary' }}>
                        <SettingsIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={'Workspace Settings'} />
                    </MenuItem>
                  ) : null}
                </Menu>
              </>
            )}
            {navItems.map((item) => (
              <React.Fragment key={item.link}>
                <MenuItem
                  selected={activeMenuItem === item.link}
                  sx={{
                    borderRadius: 2,
                    mx: 1,
                    my: 0.5,
                    color: 'text.primary',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                      color: 'text.primary'
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'action.selected'
                    }
                  }}>
                  <ListItemIcon
                    sx={{
                      color: activeMenuItem === item.link ? 'text.primary' : 'text.secondary'
                    }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    secondary={item.secondaryText}
                    onClick={() => {
                      if (item.link) {
                        navigate(item.link);
                      }
                    }}
                    sx={{
                      m: 0
                    }}
                  />
                </MenuItem>
              </React.Fragment>
            ))}
          </MenuList>
          <Box sx={{ flexGrow: 1 }} />
          {ongoingEvents.length > 0 && (
            <>
              <Typography
                variant="overline"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  mx: 2,
                  mt: 1,
                  mb: 0.5,
                  px: 1.25,
                  py: 0.25,
                  borderRadius: 1.25,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  color: 'text.secondary',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: 0.5
                }}>
                Current Ongoing Events ({ongoingEvents.length})
              </Typography>
              {ongoingEvents.map((event) => (
                <MenuItem
                  key={event.id}
                  onClick={() => navigate(`/events/${event.id}`)}
                  sx={{
                    minHeight: 32,
                    mx: 2,
                    my: 0.35,
                    pl: 2,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      mr: 1.25,
                      flexShrink: 0
                    }}
                  />
                  <ListItemText
                    primary={
                      <Box>
                        <Typography
                          noWrap
                          sx={{
                            maxWidth: 150,
                            fontSize: '0.8rem',
                            textOverflow: 'ellipsis',
                            color: 'text.primary'
                          }}>
                          {event.name || 'Untitled Event'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4 }}>
                          <LinearProgress
                            variant="determinate"
                            value={getEventProgress(event)}
                            sx={{
                              flex: 1,
                              height: 4,
                              borderRadius: 999,
                              bgcolor: 'action.hover',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 999,
                                bgcolor: 'success.main'
                              }
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                            {getEventProgress(event)}%
                          </Typography>
                        </Box>
                      </Box>
                    }
                    primaryTypographyProps={{
                      component: 'div'
                    }}
                  />
                </MenuItem>
              ))}
            </>
          )}
          <Divider />
          {authenticated && currentUserProfile && (
            <>
              <MenuItem
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  m: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
                onClick={(e) => {
                  setUserMenuAnchor(e.currentTarget);
                  setUserMenuOpen(true);
                }}>
                <ListItemIcon>
                  <img
                    src={
                      currentUserProfile.avatarUrl ||
                      'https://ui-avatars.com/api/?name=' + encodeURIComponent(fullName || 'User')
                    }
                    alt="Profile"
                    style={{ width: 32, height: 32, borderRadius: '50%' }}
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{ ml: 1 }}
                  primary={fullName}
                  secondary={currentUserProfile.email}
                />
              </MenuItem>
              <Menu
                anchorEl={userMenuAnchor}
                open={userMenuOpen}
                onClose={() => setUserMenuOpen(false)}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left'
                }}>
                {userMenuItems
                  .filter(
                    (item) => item.access === Roles.USER || hasAccess(currentUserProfile, item.access)
                  )
                  .map((item) => (
                    <MenuItem
                      key={item.text}
                      onClick={() => {
                        setUserMenuOpen(false);
                        if (item.text === 'Sign Out') {
                          handleSignOut();
                        } else {
                          navigate(item.link);
                        }
                      }}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'action.selected',
                          color: 'text.primary'
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'action.selected'
                        }
                      }}>
                      <ListItemIcon sx={{ color: 'text.secondary' }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </MenuItem>
                  ))}
              </Menu>
            </>
          )}
        </Drawer>
      )}

      {/* Mobile Menu */}
      {isMobile && (
        <Menu
          anchorEl={userMenuAnchor}
          open={userMenuOpen}
          onClose={() => setUserMenuOpen(false)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}>
          {userMenuItems
            .filter(
              (item) => item.access === Roles.USER || hasAccess(currentUserProfile, item.access)
            )
            .map((item) => (
              <MenuItem
                key={item.text}
                onClick={() => {
                  setUserMenuOpen(false);
                  if (item.text === 'Sign Out') {
                    handleSignOut();
                  } else {
                    navigate(item.link);
                  }
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                    color: 'text.primary'
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'action.selected'
                  }
                }}>
                <ListItemIcon sx={{ color: 'text.secondary' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </MenuItem>
            ))}
        </Menu>
      )}

      {/* Mobile Main Menu - Drawer from the top */}
      {isMobile && (
        <Drawer
          anchor="top"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: '100%',
              boxSizing: 'border-box',
              pb: 3,
              px: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
            {mode === 'dark' ? (
              <img src={BowlscoutWhiteLogo} alt="Logo" style={{ width: '150px', height: 'auto' }} />
            ) : (
              <img src={BowlscoutLogo} alt="Logo" style={{ width: '150px', height: 'auto' }} />
            )}
          </Box>
          <List>
            {authenticated && currentUserProfile && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={(e) => setWorkspaceMenuAnchor(e.currentTarget)}
                    sx={{
                      borderRadius: 2,
                      my: 0.5,
                      color: 'text.primary',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}>
                    <ListItemIcon>
                      <WorkspacesIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={workspaceLabel} secondary={'Current workspace'} />
                    <KeyboardArrowDownIcon fontSize="small" color="action" />
                  </ListItemButton>
                </ListItem>
                <Menu
                  anchorEl={workspaceMenuAnchor}
                  open={Boolean(workspaceMenuAnchor)}
                  onClose={() => setWorkspaceMenuAnchor(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}>
                  {(workspaces || []).map((workspace) => (
                    <MenuItem
                      key={`mobile-${workspace.id}`}
                      selected={workspace.id === currentWorkspace?.id}
                      onClick={async () => {
                        setWorkspaceMenuAnchor(null);
                        await handleSwitchWorkspace(workspace.id);
                        setMobileMenuOpen(false);
                      }}>
                      <ListItemText
                        primary={workspace.name || 'Workspace'}
                        secondary={workspace.id === currentWorkspace?.id ? 'Active' : ''}
                      />
                    </MenuItem>
                  ))}
                  {canOpenWorkspaceSettings ? <Divider /> : null}
                  {canOpenWorkspaceSettings ? (
                    <MenuItem
                      onClick={() => {
                        setWorkspaceMenuAnchor(null);
                        navigate('/workspace-settings');
                        setMobileMenuOpen(false);
                      }}>
                      <ListItemIcon sx={{ color: 'text.secondary' }}>
                        <SettingsIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={'Workspace Settings'} />
                    </MenuItem>
                  ) : null}
                </Menu>
              </>
            )}
            {navItems.map((item) => (
              <React.Fragment key={item.link}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={activeMenuItem === item.link}
                    onClick={() => {
                      if (item.link) {
                        navigate(item.link);
                      }
                      setMobileMenuOpen(false);
                    }}
                    sx={{
                      borderRadius: 2,
                      my: 0.5,
                      color: 'text.primary',
                      '& .MuiListItemIcon-root': {
                        color: 'text.secondary'
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                        '& .MuiListItemIcon-root': {
                          color: 'text.primary'
                        }
                      }
                    }}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
            {ongoingEvents.length > 0 && (
              <>
                <Typography
                  variant="overline"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    mx: 2,
                    mt: 1,
                    mb: 0.5,
                    px: 1.25,
                    py: 0.25,
                    borderRadius: 1.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: 0.5
                  }}>
                  Current Ongoing Events ({ongoingEvents.length})
                </Typography>
                {ongoingEvents.map((event) => (
                  <ListItem key={`mobile-${event.id}`} disablePadding>
                    <ListItemButton
                      onClick={() => {
                        navigate(`/events/${event.id}`);
                        setMobileMenuOpen(false);
                      }}
                      sx={{
                        borderRadius: 1.5,
                        my: 0.35,
                        ml: 2,
                        mr: 2,
                        py: 0.3,
                        px: 1.2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.default',
                        color: 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          mr: 1.1,
                          flexShrink: 0
                        }}
                      />
                      <ListItemText
                        primary={
                          <Box>
                            <Typography
                              noWrap
                              sx={{
                                maxWidth: 190,
                                fontSize: '0.8rem',
                                textOverflow: 'ellipsis',
                                color: 'text.primary'
                              }}>
                              {event.name || 'Untitled Event'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4 }}>
                              <LinearProgress
                                variant="determinate"
                                value={getEventProgress(event)}
                                sx={{
                                  flex: 1,
                                  height: 4,
                                  borderRadius: 999,
                                  bgcolor: 'action.hover',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 999,
                                    bgcolor: 'success.main'
                                  }
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                                {getEventProgress(event)}%
                              </Typography>
                            </Box>
                          </Box>
                        }
                        primaryTypographyProps={{
                          component: 'div'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </>
            )}
          </List>
        </Drawer>
      )}

      {/* Main Content */}

      <Box
        component="main"
        sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3, overflowX: 'hidden', minWidth: 0 }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
